import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MyLocation as MyLocationIcon,
  Place as PlaceIcon,
  GpsFixed as LockGpsIcon,
  GpsOff as ClearGpsIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import {
  canLockCurrentDeviceGps,
  formatGpsCoordinates,
  getDeviceDisplayName,
  getEffectiveGps,
  getReportedDeviceGps,
  gpsSourceLabel,
  hasManualGpsOverride,
} from '../utils/deviceDisplayUtils';
import { filterLastKnownGoodEligible, isLastKnownGoodEligible, LKG_INELIGIBLE_MSG } from '../utils/lkgUtils';

const LOCK_GPS_HELP =
  'Freeze the device-reported coordinates as manual GPS. The map pin will no longer follow live GPS updates.';

const DeviceLocationSelectionPanel = ({
  devices = [],
  onNotify,
  onRefreshDevice,
  onSetOnMap,
  saving = false,
}) => {
  const eligibleDevices = filterLastKnownGoodEligible(devices);
  const ineligibleCount = devices.length - eligibleDevices.length;
  const [actionLoading, setActionLoading] = useState('');

  const notify = useCallback((payload) => {
    if (typeof onNotify === 'function') {
      onNotify(payload);
    }
  }, [onNotify]);

  const refreshDevice = useCallback(async (serial) => {
    if (typeof onRefreshDevice === 'function') {
      await onRefreshDevice(serial);
    }
  }, [onRefreshDevice]);

  const handleLockOne = async (device) => {
    const serial = device.serial_number?.toLowerCase();
    const reported = getReportedDeviceGps(device);
    if (!serial || !reported || saving || actionLoading) return;

    setActionLoading(`lock-${serial}`);
    try {
      await api.patchDeviceMetadata(serial, {
        gpsOverride: { lat: reported.lat, lng: reported.lng, alt: reported.alt },
      });
      await refreshDevice(serial);
      notify({
        type: 'success',
        message: `Locked current device GPS for ${getDeviceDisplayName(device)}`,
      });
    } catch (err) {
      notify({ type: 'error', message: err.message || `Failed to lock GPS for ${serial}` });
    } finally {
      setActionLoading('');
    }
  };

  const handleClearOne = async (device) => {
    const serial = device.serial_number?.toLowerCase();
    if (!serial || saving || actionLoading) return;

    setActionLoading(`clear-${serial}`);
    try {
      await api.patchDeviceMetadata(serial, { clearGpsOverride: true });
      await refreshDevice(serial);
      notify({ type: 'success', message: `Manual GPS cleared for ${getDeviceDisplayName(device)}` });
    } catch (err) {
      notify({ type: 'error', message: err.message || `Failed to clear GPS for ${serial}` });
    } finally {
      setActionLoading('');
    }
  };

  const handleLockAll = async () => {
    const targets = eligibleDevices.filter(canLockCurrentDeviceGps);
    if (targets.length === 0) {
      notify({ type: 'warning', message: 'No devices with lockable device-reported GPS' });
      return;
    }

    setActionLoading('lock-all');
    let ok = 0;
    const errors = [];
    try {
      for (const device of targets) {
        const serial = device.serial_number?.toLowerCase();
        const reported = getReportedDeviceGps(device);
        if (!serial || !reported) continue;
        try {
          await api.patchDeviceMetadata(serial, {
            gpsOverride: { lat: reported.lat, lng: reported.lng, alt: reported.alt },
          });
          await refreshDevice(serial);
          ok += 1;
        } catch (err) {
          errors.push(`${serial}: ${err.message || 'failed'}`);
        }
      }
      notify({
        type: ok === targets.length ? 'success' : (ok > 0 ? 'warning' : 'error'),
        message: `Locked current device GPS for ${ok}/${targets.length} device(s)${
          errors.length ? ` (${errors.length} failed)` : ''
        }`,
      });
    } finally {
      setActionLoading('');
    }
  };

  if (devices.length === 0) {
    return <Alert severity="info">No devices selected.</Alert>;
  }

  const lockableCount = eligibleDevices.filter(canLockCurrentDeviceGps).length;
  const manualCount = eligibleDevices.filter(hasManualGpsOverride).length;

  return (
    <Box>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MyLocationIcon sx={{ color: '#1565c0' }} />
            Device location
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Lock the current device-reported GPS as a manual pin, set a new location on the map, or clear a manual override.
            {LOCK_GPS_HELP}
          </Typography>

          {ineligibleCount > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {ineligibleCount} selected device(s) are not jnet2 CAP/Node and are excluded. {LKG_INELIGIBLE_MSG}
            </Alert>
          )}

          {eligibleDevices.length === 0 ? (
            <Alert severity="warning">{LKG_INELIGIBLE_MSG}</Alert>
          ) : (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip label={`${eligibleDevices.length} eligible`} size="small" color="primary" />
                <Chip label={`${lockableCount} can lock current GPS`} size="small" variant="outlined" />
                <Chip label={`${manualCount} manual GPS`} size="small" variant="outlined" />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 3 }}>
                <Tooltip title={LOCK_GPS_HELP}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={actionLoading === 'lock-all' ? <CircularProgress size={16} color="inherit" /> : <LockGpsIcon />}
                      disabled={!!actionLoading || saving || lockableCount === 0}
                      onClick={handleLockAll}
                    >
                      Lock current GPS for all
                    </Button>
                  </span>
                </Tooltip>
              </Stack>

              <Stack spacing={1.5}>
                {eligibleDevices.map((device) => {
                  const serial = device.serial_number?.toLowerCase();
                  const effective = getEffectiveGps(device);
                  const reported = getReportedDeviceGps(device);
                  const busy = actionLoading === `lock-${serial}` || actionLoading === `clear-${serial}`;
                  const source = device.gps_source || effective?.source;

                  return (
                    <Card key={serial} variant="outlined" sx={{ backgroundColor: '#fafafa' }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="subtitle2">{getDeviceDisplayName(device)}</Typography>
                            {device.name && (
                              <Typography variant="caption" color="text.secondary">{serial}</Typography>
                            )}
                            <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.75 }}>
                              {source && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={gpsSourceLabel(source)}
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                              )}
                              {effective && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`Map: ${formatGpsCoordinates(effective)}`}
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                              )}
                              {reported && !hasManualGpsOverride(device) && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  label={`Device: ${formatGpsCoordinates(reported)}`}
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                              )}
                            </Stack>
                          </Box>

                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {canLockCurrentDeviceGps(device) && (
                              <Tooltip title={LOCK_GPS_HELP}>
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={actionLoading === `lock-${serial}` ? <CircularProgress size={14} color="inherit" /> : <LockGpsIcon />}
                                    disabled={busy || !!actionLoading || saving}
                                    onClick={() => handleLockOne(device)}
                                  >
                                    Lock current GPS
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PlaceIcon />}
                              disabled={busy || !!actionLoading || saving}
                              onClick={() => onSetOnMap?.(device)}
                            >
                              Set on map
                            </Button>
                            {hasManualGpsOverride(device) && (
                              <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                startIcon={actionLoading === `clear-${serial}` ? <CircularProgress size={14} color="inherit" /> : <ClearGpsIcon />}
                                disabled={busy || !!actionLoading || saving}
                                onClick={() => handleClearOne(device)}
                              >
                                Clear manual GPS
                              </Button>
                            )}
                          </Stack>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DeviceLocationSelectionPanel;
export { isLastKnownGoodEligible };
